import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const API = "https://laravel-api.aj-mart.com";

function imageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const clean = String(path).replace(/^\/+/, "");
  if (clean.startsWith("storage/")) return `${API}/${clean}`;
  return `${API}/storage/app/public/${clean}`;
}

function OfferCard({ item, isArabic, t }) {
  const name = (isArabic ? item.nameAr || item.nameEn : item.nameEn || item.nameAr) || "";
  const description = (isArabic ? item.descAr || item.descEn : item.descEn || item.descAr) || "";
  const shortDesc = String(description).replace(/\\n|\n|\\ة/g, " ").replace(/\s+/g, " ").trim();
  const discount = Math.max(0, Math.min(100, item.discount));
  const finalPrice = item.price * (1 - discount / 100);
  const inStock = item.stock > 0;

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-[#e6eef5] bg-white shadow-[0_8px_28px_rgba(21,70,110,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(21,70,110,0.14)]">
      <div className="relative m-3 mb-0 flex h-[220px] items-center justify-center overflow-hidden rounded-[18px] bg-gradient-to-br from-[#f5fbff] via-[#f9fbff] to-[#eaf4fc] sm:h-[245px]">
        {item.image ? (
          <img src={imageUrl(item.image)} alt={name} loading="lazy" className="h-full w-full object-contain p-5 transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <span className="text-sm text-slate-400">{isArabic ? "لا توجد صورة" : "No image"}</span>
        )}
        {discount > 0 && (
          <span className="absolute left-3 top-3 rounded-full bg-[#ff3349] px-3 py-1.5 text-xs font-extrabold text-white shadow-sm" dir="ltr">
            {discount}% OFF
          </span>
        )}
        <span className="absolute bottom-3 left-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#087cba] shadow-sm">
          {item.type === "accessory" ? (isArabic ? "إكسسوار" : "Accessory") : (isArabic ? "منتج" : "Product")}
        </span>
      </div>

      <div className="flex flex-1 flex-col px-4 pb-4 pt-4 sm:px-5">
        <Link to={item.url} className="mb-2 line-clamp-2 min-h-[52px] text-[17px] font-extrabold leading-[1.5] text-[#101a2b] transition-colors hover:text-[#078fcb]">
          {name}
        </Link>
        <p className="mb-4 line-clamp-2 min-h-[44px] text-[13px] leading-[1.7] text-[#718096]">{shortDesc}</p>

        <div className="mt-auto">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1" dir="ltr">
            <span className="text-[21px] font-extrabold text-[#087ec0]">{finalPrice.toFixed(2)} <span className="text-sm">{t("JOD")}</span></span>
            {discount > 0 && <span className="text-[13px] text-[#9ba6b6] line-through">{item.price.toFixed(2)} {t("JOD")}</span>}
          </div>
          <div className={`my-3 flex items-center gap-2 text-[13px] font-semibold ${inStock ? "text-[#12a538]" : "text-red-500"}`}>
            <span className={`h-2 w-2 rounded-full ${inStock ? "bg-[#12a538]" : "bg-red-500"}`} />
            {inStock ? t("In Stock") : t("Out of Stock")}
          </div>
          <Link to={item.url} className="flex h-[44px] w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#087ec0] to-[#05a0df] text-sm font-bold text-white shadow-[0_5px_12px_rgba(0,142,210,0.2)] transition-all hover:brightness-110">
            {isArabic ? "عرض التفاصيل" : "View Offer"}
            <span className="ms-2" aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function OffersSection() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchOffers = async () => {
      const [productsResult, accessoriesResult] = await Promise.allSettled([
        axios.get(`${API}/api/products`),
        axios.get(`${API}/api/accessories`),
      ]);

      if (productsResult.status === "rejected") console.error("Products API:", productsResult.reason);
      if (accessoriesResult.status === "rejected") console.error("Accessories API:", accessoriesResult.reason);

      const productRows = productsResult.status === "fulfilled" ? productsResult.value.data?.products : [];
      const accessoryRows = accessoriesResult.status === "fulfilled" ? accessoriesResult.value.data?.accessories : [];

      const productOffers = (Array.isArray(productRows) ? productRows : [])
        .filter((p) => Number(p.is_news_offer) === 1)
        .map((p) => ({
          id: `product-${p.product_id}`,
          type: "product",
          url: `/productdetails/${p.product_id}`,
          nameAr: p.product_name_A,
          nameEn: p.product_name_E,
          descAr: p.product_desc_A,
          descEn: p.product_desc_E,
          price: Number(p.product_price) || 0,
          discount: Number(p.product_price_discount) || 0,
          stock: Number(p.product_stock_available) || 0,
          image: p.product_main_img,
        }));

      const accessoryOffers = (Array.isArray(accessoryRows) ? accessoryRows : [])
        .filter((a) => a.is_special_offer === true || Number(a.is_special_offer) === 1)
        .map((a) => ({
          id: `accessory-${a.accessories_id}`,
          type: "accessory",
          url: `/accessoriesdetails/${a.accessories_id}`,
          nameAr: a.accessories_name_A,
          nameEn: a.accessories_name_E,
          descAr: a.accessories_desc_A,
          descEn: a.accessories_desc_E,
          price: Number(a.accessories_price) || 0,
          discount: Number(a.accessories_price_discount) || 0,
          stock: Number(a.accessories_stock_available) || 0,
          image: a.accessories_main_img,
        }));

      if (mounted) {
        setOffers([...productOffers.reverse(), ...accessoryOffers.reverse()]);
        setLoading(false);
      }
    };
    fetchOffers();
    return () => { mounted = false; };
  }, []);

  if (!loading && offers.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f7fbff] to-white py-10 sm:py-14" dir={isArabic ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-[28px] font-black tracking-tight text-[#0c1732] sm:text-[36px]">
              {t("News")} <span className="text-[#079ad7]">{t("Offers")}</span>
            </h2>
            <p className="mt-1 text-[13px] text-[#718096] sm:text-[15px]">
              {isArabic ? "اكتشف أحدث العروض على المنتجات والإكسسوارات" : "Discover the latest offers on products and accessories"}
            </p>
            <div className="mt-3 h-[3px] w-12 rounded-full bg-[#079ad7]" />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="offers-prev flex h-10 w-10 items-center justify-center rounded-full border border-[#d9e6f0] bg-white text-xl text-[#0d2640] shadow-sm hover:bg-[#edf7ff]" aria-label="Previous offers">‹</button>
            <button type="button" className="offers-next flex h-10 w-10 items-center justify-center rounded-full border border-[#d9e6f0] bg-white text-xl text-[#0d2640] shadow-sm hover:bg-[#edf7ff]" aria-label="Next offers">›</button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((n) => <div key={n} className="h-[470px] animate-pulse rounded-[22px] bg-[#e9f1f8]" />)}
          </div>
        ) : (
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            spaceBetween={18}
            slidesPerView={1.15}
            navigation={{ prevEl: ".offers-prev", nextEl: ".offers-next" }}
            pagination={{ clickable: true, el: ".offers-pagination" }}
            autoplay={offers.length > 1 ? { delay: 3500, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
            loop={offers.length > 5}
            breakpoints={{
              480: { slidesPerView: 1.5 },
              640: { slidesPerView: 2 },
              900: { slidesPerView: 3 },
              1200: { slidesPerView: 4 },
              1500: { slidesPerView: 5 },
            }}
            className="!overflow-visible !pb-3"
          >
            {offers.map((item) => (
              <SwiperSlide key={item.id} className="!h-auto">
                <OfferCard item={item} isArabic={isArabic} t={t} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        <div className="offers-pagination mt-6 flex min-h-4 items-center justify-center gap-1 [&_.swiper-pagination-bullet-active]:!bg-[#078fcb]" />
      </div>
    </section>
  );
}
